import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ButtonLink from '@components/button-link/ButtonLink';
import HomesCarousel from '@components/carousels/HomesCarousel';
import EmailSignup from '@components/email-signup/EmailSignup';
import Hero from '@components/hero/Hero';
import TextMedia, { Media, Text } from '@components/text-media/TextMedia';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import IconArrowRight from '@ui/icons/imaterial/arrows/Arrow Right.svg';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import React from 'react';

import { DefaultFirestoreConverter } from '../../../../../types/DefaultFirestoreConverter';
import useStyles from './featured.style';

const title = 'Staging a Home is Different with Guest House';
const description = 'When clients choose us, they find out staging a home is different with Guest House. Look at the houses we\'ve staged to see what we mean.';

async function getFeaturedHomesByVicinity(vicinity: string | string[]): Promise<guesthouse.Home[]> {
  const whereVicinity = Array.isArray(vicinity)
    ? where('vicinity', 'in', vicinity)
    : where('vicinity', '==', vicinity);

  const homesSnapshot = await getDocs<guesthouse.Home>(query(collection(database, 'homes').withConverter(new DefaultFirestoreConverter<guesthouse.Home>())
    ,where('published', '==', true)
    ,where('featured', '==', true)
    ,whereVicinity
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  return homesSnapshot.docs.map(h => h.data());
}

export const getServerSideProps: GetServerSideProps = async () => {
  const denverHomes = await getFeaturedHomesByVicinity('Denver');
  const boulderHomes = await getFeaturedHomesByVicinity('Boulder');
  const sanDiegoHomes = await getFeaturedHomesByVicinity(['San Diego', 'Coronado', 'Encinitas', 'Norwalk', 'Huntington Beach', 'Anaheim', 'Escondido', 'Mission Viejo']);
  const vailHomes = await getFeaturedHomesByVicinity('Vail');

  const data = {
    denverHomes: JSON.stringify(denverHomes),
    boulderHomes: JSON.stringify(boulderHomes),
    sanDiegoHomes: JSON.stringify(sanDiegoHomes),
    vailHomes: JSON.stringify(vailHomes)
  };

  return { props: { data } };
};

function FeaturedHomes(props) {
  const { classes, theme } = useStyles();
  const denverHomes = JSON.parse(props.data.denverHomes) as guesthouse.Home[];
  const boulderHomes = JSON.parse(props.data.boulderHomes) as guesthouse.Home[];
  const sanDiegoHomes = JSON.parse(props.data.sanDiegoHomes) as guesthouse.Home[];
  const vailHomes = JSON.parse(props.data.vailHomes) as guesthouse.Home[];
  const { md } = useBreakpoints();

  return (
    <>
      <AppBackgroundColor backgroundColor="#ede4e2" />
      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description })} />
      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key="homes"
          backgroundImage="/images/shop-homes/featured-homes.jpeg"
          className={classes.hero}
        >
          <Box
            textAlign="center"
            maxWidth={700}
          >
            <Typography
              variant={md ? 'display2' : 'display3'}
              component="h1"
              style={{
                color: theme.palette.common.white,
                textShadow: '0px 0px 20px rgba(0, 0, 0, .7)',
              }}
            >
              Featured Homes
            </Typography>
          </Box>
        </Hero>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ overflowX: 'hidden' }}
        margin={0}
      >
        <ContentBlock
          margin={[0, theme.spacing(0)]}
          marginMd={[0, theme.spacing(12)]}
          padding={0}
          paddingMd={[0, theme.spacing(3)]}
        >
          {!!denverHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h4Alt"
                component="h2"
                style={{ marginBottom: theme.spacing(4) }}
              >
                Denver
              </Typography>
            </div>

            <HomesCarousel
              homes={denverHomes}
              usePhotosLink={true}
            />

          </ContentBlock>
          )}

          {!!sanDiegoHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h4Alt"
                component="h2"
                style={{ marginBottom: theme.spacing(4) }}
              >
                Southern California
              </Typography>
            </div>

            <HomesCarousel
              homes={sanDiegoHomes}
              usePhotosLink={true}
            />
          </ContentBlock>
          )}

          {!!boulderHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h4Alt"
                component="h2"
                style={{ marginBottom: theme.spacing(4) }}
              >
                Boulder
              </Typography>
            </div>

            <HomesCarousel
              homes={boulderHomes}
              usePhotosLink={true}
            />
          </ContentBlock>
          )}

          {!!vailHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h4Alt"
                component="h2"
                style={{ marginBottom: theme.spacing(4) }}
              >
                Vail
              </Typography>
            </div>

            <HomesCarousel
              homes={vailHomes}
              usePhotosLink={true}
            />
          </ContentBlock>
          )}
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        margin={[8, 0]}
        marginMd={[24, 0, 24]}
      >
        <ContentBlock
          maxWidth={1365}
          margin={[0, 'auto']}
          padding={[12, 0]}
        >
          <Grid
            container
            style={{ marginBottom: theme.spacing(8) }}
          >
            <Grid
              item
              xs={12}
              md={7}
            >
              <Typography
                component="h2"
                variant="display4Alt"
              >
                Join our Exclusive Realtor&nbsp;List
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
            >
              <Typography
                gutterBottom
                style={{ fontSize: md ? 20 : 16, lineHeight: 1.5, marginTop: theme.spacing(2), marginBottom: theme.spacing(4) }}
              >
                Sign up for newly listed featured homes, industry news, offers and special events. Unsubscribe anytime.
              </Typography>

              <EmailSignup
                campaignList="realtors"
              />
            </Grid>
          </Grid>
        </ContentBlock>

        <TextMedia style={{ background: '#f6f6f7' }}>
          <Text style={{ justifyContent: 'center' }}>
            <Typography
              gutterBottom
              variant="h1"
              component="h2"
            >
              Stage a home
              <br />
              {' '}
              with Guest House
            </Typography>

            <Typography style={{ flex: 0, marginBottom: theme.spacing(6) }}>
              We&apos;re helping properties in Denver, Boulder, Colorado Springs and San Diego sell 40% faster. Let&apos;s work together.
            </Typography>

            <div>
              <Button
                size="large"
                variant="contained"
                color="primary"
                component={ButtonLink}
                href="/home-staging/get-a-quote"
                style={{
                  marginRight: theme.spacing(2)
                }}
              >
                Get a Free Quote
              </Button>

              <Button
                component={ButtonLink}
                href="/home-staging"
                size="large"
                style={{ color: theme.palette.tertiary.main, marginRight: theme.spacing(1) }}
              >
                How it Works
                <IconArrowRight
                  style={{
                    marginLeft: theme.spacing(1),
                    height: 18,
                    fill: theme.palette.tertiary.main,
                    position: 'relative',
                    top: 2,
                  }}
                />
              </Button>
            </div>
          </Text>

          <Media
            orientation="right"
          >
            <div
              style={{
                borderRadius: theme.shape.borderRadius,
                overflow: 'hidden',
                height: md ? 750 : 350,
                width: '100%',
                position: 'relative',
                top: md ? -90 : 0,
              }}
            >
              <Image
                fill
                src="/images/shop-homes/home-stage.jpg"
                alt=""
                sizes="100vw"
                style={{
                  objectFit: 'cover'
                }}
              />
            </div>
          </Media>
        </TextMedia>
      </ContentBlock>
    </>
  );
}


export default FeaturedHomes;
